import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAdminTransmissionAct, IBaseAdminTransmissionAct } from 'types/income';
import { ContentLanguage, TableDataResponse } from 'types/general';
import { exportPdfConfig, exportXlsxConfig, getSorting } from 'utils/general';
import { AdminTransmissionActsFilters } from './admin-transmission-acts.types';

const getFilters = (filters: Partial<AdminTransmissionActsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...getSorting(filters.sort),
  filter: {
    parentCompanyId: filters.parentCompany,
    companyIds: filters.companies,
    dateFrom: filters.date?.from,
    dateTo: filters.date?.to,
  },
});

interface GetTransmissionActs {
  filters: Partial<AdminTransmissionActsFilters>;
}

interface GetTransmissionAct {
  transmissionActId: string;
}

interface ExportXls {
  transmissionActId: string;
}

interface DownloadPdf {
  transmissionActId: string;
}

interface SendPdf {
  transmissionActId: string;
  data: {
    recipients: string[];
    language: Uppercase<ContentLanguage>;
    contentText: string;
  };
}

interface UpdateTransmissionAct {
  transmissionActId: string;
  data: {
    openingBalance: number;
    closingBalance: number;
  };
}

interface RegenerateTransmissionActs {
  clientId: string;
  data: {
    month: number;
    year: number;
    companyId?: string;
  };
}

export const loaders = {
  getTransmissionActs: ({ filters }: GetTransmissionActs) => {
    return api<TableDataResponse<IBaseAdminTransmissionAct>>({
      url: Endpoints.melpAdmin.transmissionActs.root,
      context: 'melp',
      params: getFilters(filters),
    });
  },
  getTransmissionAct: ({ transmissionActId }: GetTransmissionAct) => {
    return api<IAdminTransmissionAct>({
      url: Endpoints.melpAdmin.transmissionActs.byId.root.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      context: 'melp',
    });
  },
  updateTransmissionAct: ({
    transmissionActId,
    data,
  }: UpdateTransmissionAct) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byId.root.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      context: 'melp',
      data: { ...data },
      method: 'patch',
    });
  },
  regenerateTransmissionActs: ({
    clientId,
    data,
  }: RegenerateTransmissionActs) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byClient.replace(
        ':clientId',
        clientId,
      ),
      context: 'melp',
      data,
      method: 'post',
    });
  },
  downloadPdf: ({ transmissionActId }: DownloadPdf) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byId.export.pdf.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      context: 'melp',
      ...exportPdfConfig,
    });
  },
  sendPdf: ({ transmissionActId, data }: SendPdf) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byId.send.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      data,
      context: 'melp',
      method: 'post',
    });
  },
  exportXls: ({ transmissionActId }: ExportXls) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byId.export.xls.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      context: 'melp',
      ...exportXlsxConfig,
    });
  },
};
